/* eslint-disable no-useless-escape, no-unused-vars */
import React, { useMemo, Suspense } from 'react';
import { isEmpty, intersection } from 'lodash';
import mapStyles from './Map.module.scss';
import { useFilters } from '@panwds/react-filters';
import MapStatusBar, { STATUS_TYPE_ALL_SELECTED } from './MapStatusBar';
import { useFetchProducts } from '../../../services/api/products';
import { parseRegionFilterSelections, normalizeCountry } from '../../../shared/utils/regions';
import { resolveRegionStatus } from './mapStatusHelpers';

const GoogleStatusMap = React.lazy(() => import('./GoogleStatusMap'));

const FILTER_CONTEXT_ID = 'StatusPageFilterBar';

const getRegionLongName = (region = {}) => {
    const {
        city,
        edge_location: edgeLocation,
    } = region;

    let longName = city;

    if (edgeLocation) {
        longName += ` - ${edgeLocation}`;
    }

    return longName;
};

const MapContainer = props => {
    const {
        mapBoxToken,
        googleMapsKey,
        regions: propsRegions,
        planeType,
        statusType,
        mapIncidents,
        mapIncidentsUpdatedTime,
        refetchMapIncidents,
        isFetchingMapIncidents,
        statusesByProductCity,
        incidentsByProductCity,
    } = props;

    const { filters: pageFilters, initialized: isFilterBarInitialized } = useFilters(FILTER_CONTEXT_ID);

    const {
        data: products,
        allRegions: apiRegions,
        productsById,
        isLoading: isLoadingProducts,
        isFetching: isFetchingProducts,
    } = useFetchProducts({
        isEnabled: !!isFilterBarInitialized,
        gcTime: 5 * 60 * 1000,
        refetchInterval: 10 * 60 * 1000,
    });

    const regions = apiRegions?.length > 0 ? apiRegions : propsRegions;
    const selectedProducts = pageFilters?.products || [];

    const filteredRegions = useMemo(() => {
        if (!isFilterBarInitialized || !regions) {
            return null;
        }

        const selectedRegions = pageFilters?.region || [];

        if (isEmpty(selectedRegions) && isEmpty(selectedProducts)) {
            return regions;
        }

        const {
            selectedContinents,
            selectedCountries,
            selectedCities,
        } = parseRegionFilterSelections(selectedRegions);

        const hasRegionFilter = selectedContinents.length || selectedCountries.length || selectedCities.length;

        const tmpFilteredRegions = regions.filter(regionObj => {
            if (selectedProducts?.length) {
                const productMapping = regionObj?.product_mapping;
                if (!productMapping || !Array.isArray(productMapping) || productMapping.length === 0) {
                    return false;
                }
                const overlaps = intersection(selectedProducts, productMapping);
                if (!overlaps?.length) {
                    return false;
                }
            }

            if (hasRegionFilter) {
                const matchesRegion =
                    (selectedContinents.length > 0 && selectedContinents.includes(regionObj?.continent)) ||
                    (selectedCountries.length > 0 && selectedCountries.includes(normalizeCountry(regionObj?.country))) ||
                    (selectedCities.length > 0 && selectedCities.includes(regionObj?.city));

                if (!matchesRegion) {
                    return false;
                }
            }

            return true;
        });

        return tmpFilteredRegions;
    }, [regions, pageFilters, isFilterBarInitialized, selectedProducts]);

    const mapData = useMemo(() => {
        const tmpMapConfigs = {
            "type": "FeatureCollection",
            "crs": { "type": "name", "properties": { "name": "urn:ogc:def:crs:OGC:1.3:CRS84" } },
            "features": [],
        };

        if (!filteredRegions?.length) {
            return tmpMapConfigs;
        }

        filteredRegions.forEach((region, ndx) => {
            const {
                longitude,
                latitude,
                city,
                city: edgeLocation,
                prisma_access_location: paLocation,
                product_mapping: productMapping,
                region_id: regionId,
            } = region;

            const tmpCoords = [longitude, latitude];
            const regionKey = `${edgeLocation}-${city}-${paLocation || ''}`;
            const longName = getRegionLongName(region);
            const productCSV = productMapping?.length ? productMapping.join(', ') : '';

            const effectiveProductMapping = selectedProducts?.length
                ? productMapping?.filter(p => selectedProducts.includes(p))
                : productMapping;

            let regionStatus = resolveRegionStatus(statusesByProductCity, productMapping, edgeLocation, selectedProducts);

            // FIXME: stubbing in some disruption
            if (sessionStorage.getItem('debug') === '1') {
                if (ndx % 7 === 0) {
                    regionStatus = 'disruption';
                }

                if (ndx % 5 === 0) {
                    regionStatus = 'degraded';
                }
            }

            const configItem = {
                id: regionId,
                type: 'Feature',
                properties: {
                    ...region,
                    id: regionId,
                    name: longName,
                    status: regionStatus,
                    longName,
                    regionKey,
                    productCSV,
                    product_mapping: effectiveProductMapping,
                },
                geometry: {
                    type: 'Point',
                    coordinates: [...tmpCoords, 100]
                },
            };

            tmpMapConfigs.features.push(configItem);
        });

        return tmpMapConfigs;
    }, [filteredRegions, statusesByProductCity, selectedProducts]);

    // Counts shown in the status bar pills must reflect all locations for the
    // current plane type — independent of the active status filter — so the
    // numbers don't jump when a pill is toggled.
    const planeFilteredMapData = useMemo(() => {
        if (planeType === 'all') return mapData;
        const filtered = { ...mapData };
        filtered.features = mapData.features.filter(feature => {
            const pt = feature.properties?.plane_type;
            if (planeType === 'data_plane') return pt === 'data';
            if (planeType === 'control_plane') return pt === 'control';
            if (planeType === 'service_plane') return pt === 'service';
            return true;
        });
        return filtered;
    }, [mapData, planeType]);

    const filteredMapData = useMemo(() => {
        const tmpMapData = { ...mapData };

        if (planeType === 'all' && (statusType === 'all' || statusType === STATUS_TYPE_ALL_SELECTED)) {
            return tmpMapData;
        }

        tmpMapData.features = mapData.features.filter(feature => {
            const tmpProperties = feature.properties;
            const tmpStatus = tmpProperties.status || 'available';

            if (statusType && statusType !== 'all' && statusType !== STATUS_TYPE_ALL_SELECTED) {
                const allowedStatuses = new Set(statusType.split(',').filter(Boolean));
                if (!allowedStatuses.has(tmpStatus)) {
                    return false;
                }
            }

            if (planeType === 'all') {
                return true;
            }

            if (planeType === 'data_plane' && tmpProperties?.plane_type === 'data') {
                return true;
            }

            if (planeType === 'control_plane' && tmpProperties?.plane_type === 'control') {
                return true;
            }

            if (
                planeType === 'service_plane' &&
                tmpProperties?.plane_type === 'service'
            ) {
                return true;
            }

            return false;
        });

        return tmpMapData;
    }, [mapData, planeType, statusType]);

    const isLoading = isFetchingMapIncidents || isFetchingProducts || !isFilterBarInitialized;

    return (
        <>
            <MapStatusBar
                incidents={mapIncidents}
                filteredMapData={filteredMapData}
                allMapData={planeFilteredMapData}
                planeType={planeType}
                statusType={statusType}
                incidentsUpdatedTime={mapIncidentsUpdatedTime}
                handleRefresh={refetchMapIncidents}
            />
            <div className="ssmutw-relative">
                {isLoading && <div className={mapStyles.mapBlurOverlay} />}
                {/* Mapbox version removed — Google Maps is now the active implementation */}
                <Suspense fallback={<div className={mapStyles.mapSkeleton} />}>
                    <GoogleStatusMap
                        googleMapsKey={googleMapsKey}
                        filteredMapData={filteredMapData}
                        planeType={planeType}
                        statusType={statusType}
                        selectedProducts={selectedProducts}
                        statusesByProductCity={statusesByProductCity}
                        incidentsByProductCity={incidentsByProductCity}
                    />
                </Suspense>
            </div>
        </>
    );
};

export default React.memo(MapContainer);
